import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

type FieldValue =
  | string
  | number
  | boolean
  | string[]
  | Record<string, unknown>;

interface BulkEditContextValue {
  enabled: boolean;
  staged: Record<string, FieldValue>;
  /**
   * Snapshot of each field's value at the moment it first staged. The
   * bulk-save diff uses this — not the raw dealInfo — so fields seeded
   * from overview fallbacks or AI placeholders aren't reported as
   * "changed" when the user never touched them.
   */
  baseline: Record<string, FieldValue>;
  /**
   * `baselineValue` is for fields that only stage on commit (the Fundraising
   * dialog): their first stage is already the edit, so without the pre-edit
   * value the save diff would see no change and drop it.
   */
  stage: (
    fieldName: string,
    fieldValue: FieldValue,
    baselineValue?: FieldValue
  ) => void;
  reset: () => void;
  setEnabled: (next: boolean) => void;
}

const BulkEditContext = createContext<BulkEditContextValue | null>(null);

/**
 * Content equality for staged values. Primitives compare by identity; arrays
 * (sub_industries, business_model, …) compare element-wise because the
 * primitives re-derive them with `useMemo` and hand us a fresh array whenever
 * their draft changes identity. Without this, re-staging an unchanged value
 * would allocate a new `staged` object and re-render every field.
 */
function sameStagedValue(a: FieldValue | undefined, b: FieldValue): boolean {
  if (a === b) return true;
  if (Array.isArray(a) && Array.isArray(b)) {
    return a.length === b.length && a.every((v, i) => v === b[i]);
  }
  return false;
}

/**
 * Wraps a region (e.g. ProfileTab) so child fields can stage edits into a
 * single batch instead of saving immediately on each blur. The wrapping
 * region drives `enabled` from a top-level Edit/Save/Cancel header, then
 * commits `staged` in one go via Promise.all on Save.
 */
export function BulkEditProvider({ children }: { children: ReactNode }) {
  const [enabled, setEnabled] = useState(false);
  const [staged, setStaged] = useState<Record<string, FieldValue>>({});
  const [baseline, setBaseline] = useState<Record<string, FieldValue>>({});

  // `stage` and `reset` must keep a stable identity: every primitive stages
  // from a `useEffect` that depends on them, so an identity that changed with
  // `staged` would make each stage re-run every effect, which stages again —
  // an update loop that pegs the CPU for as long as bulk-edit is open.
  const stage = useCallback(
    (fieldName: string, fieldValue: FieldValue, baselineValue?: FieldValue) => {
      setBaseline((prev) =>
        fieldName in prev
          ? prev
          : { ...prev, [fieldName]: baselineValue ?? fieldValue }
      );
      setStaged((prev) =>
        fieldName in prev && sameStagedValue(prev[fieldName], fieldValue)
          ? prev
          : { ...prev, [fieldName]: fieldValue }
      );
    },
    []
  );

  const reset = useCallback(() => {
    setStaged({});
    setBaseline({});
  }, []);

  const value = useMemo<BulkEditContextValue>(
    () => ({ enabled, staged, baseline, setEnabled, stage, reset }),
    [enabled, staged, baseline, stage, reset]
  );

  return (
    <BulkEditContext.Provider value={value}>
      {children}
    </BulkEditContext.Provider>
  );
}

/** Returns the bulk-edit context if a provider is mounted, else null. */
export function useBulkEditMaybe(): BulkEditContextValue | null {
  return useContext(BulkEditContext);
}

/** Throws if no provider is mounted. Use only from regions that require one. */
export function useBulkEdit(): BulkEditContextValue {
  const ctx = useContext(BulkEditContext);
  if (!ctx) {
    throw new Error("useBulkEdit must be used inside <BulkEditProvider>");
  }
  return ctx;
}

/**
 * Stages the primitive's local `draft` into bulk-edit state whenever it
 * changes. No-op when no provider is mounted or bulk mode is off, so
 * primitives can call this unconditionally without affecting the regular
 * per-field save flow.
 */
export function useStageDraftInBulk(fieldName: string, draft: FieldValue) {
  const bulk = useContext(BulkEditContext);
  const enabled = bulk?.enabled ?? false;
  const stage = bulk?.stage;
  useEffect(() => {
    if (!enabled || !stage) return;
    stage(fieldName, draft);
  }, [enabled, stage, fieldName, draft]);
}
